import { TranslationProvider } from "@/contexts/TranslationContext";
import LanguageSelector from "@/components/LanguageSelector";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import DynamicFavicon from "./components/DynamicFavicon";
import Home from "./pages/Home";
import About from "./pages/About";
import Transfers from "./pages/Transfers";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminSettings from "./pages/AdminSettings";
import AdminCategories from "./pages/AdminCategories";
import AdminPaymentMethods from "./pages/AdminPaymentMethods";
import AdminEmailSettings from "./pages/AdminEmailSettings";
import AdminEmailTemplates from "./pages/AdminEmailTemplates";
import AdminTours from "./pages/AdminTours";
import AdminTransfers from "./pages/AdminTransfers";
import AdminBookings from "./pages/AdminBookings";
import AdminTourBookings from "./pages/AdminTourBookings";
import AdminVehicleImages from "./pages/AdminVehicleImages";
import AdminBlogs from "./pages/AdminBlogs";
import AdminLanguages from "./pages/AdminLanguages";
import AdminTranslationDictionary from "./pages/AdminTranslationDictionary";
import AdminTranslationSettings from "./pages/AdminTranslationSettings";
import AdminTestimonials from "./pages/AdminTestimonials";
import { AdminSeoConfigurations } from "./pages/AdminSeoConfigurations";
import AdminRentalVehicles from "./pages/AdminRentalVehicles";
import AdminRentalBookings from "./pages/AdminRentalBookings";
import AdminTransferDestinations from "./pages/AdminTransferDestinations";
import AdminTransferExtras from "./pages/AdminTransferExtras";
import TransferDestinations from "./pages/TransferDestinations";
import TransferDestinationPage from "./pages/TransferDestinationPage";
import { BookingDetails } from "./pages/BookingDetails";
import BookingConfirmation from "./pages/BookingConfirmation";
import PayPalSuccess from "./pages/PayPalSuccess";
import PayPalCancel from "./pages/PayPalCancel";
import AdminProperties from "./pages/AdminProperties";
import AdminPropertyInquiries from "./pages/AdminPropertyInquiries";
import AdminHeroSlides from "./pages/AdminHeroSlides";
import AdminPageContent from "./pages/AdminPageContent";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <TranslationProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <DynamicFavicon />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/transfers" element={<Transfers />} />
            <Route path="/traslados" element={<TransferDestinations />} />
            <Route path="/traslados/:slug" element={<TransferDestinationPage />} />
            <Route path="/booking" element={<BookingDetails />} />
            <Route path="/contact" element={<Contact />} />
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/tours" element={<AdminTours />} />
            <Route path="/admin/categories" element={<AdminCategories />} />
            <Route path="/admin/transfers" element={<AdminTransfers />} />
            <Route path="/admin/bookings" element={<AdminBookings />} />
            <Route path="/admin/tour-bookings" element={<AdminTourBookings />} />
            <Route path="/admin/vehicle-images" element={<AdminVehicleImages />} />
            <Route path="/admin/blogs" element={<AdminBlogs />} />
            <Route path="/admin/languages" element={<AdminLanguages />} />
            <Route path="/admin/translation-dictionary" element={<AdminTranslationDictionary />} />
            <Route path="/admin/translation-settings" element={<AdminTranslationSettings />} />
            <Route path="/admin/testimonials" element={<AdminTestimonials />} />
            <Route path="/admin/properties" element={<AdminProperties />} />
            <Route path="/admin/property-inquiries" element={<AdminPropertyInquiries />} />
            <Route path="/admin/rental-vehicles" element={<AdminRentalVehicles />} />
            <Route path="/admin/rental-bookings" element={<AdminRentalBookings />} />
            <Route path="/admin/transfer-destinations" element={<AdminTransferDestinations />} />
            <Route path="/admin/transfer-extras" element={<AdminTransferExtras />} />
            <Route path="/admin/payment-methods" element={<AdminPaymentMethods />} />
            <Route path="/admin/email-settings" element={<AdminEmailSettings />} />
            <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/admin/seo-configurations" element={<AdminSeoConfigurations />} />
            <Route path="/admin/hero-slides" element={<AdminHeroSlides />} />
            <Route path="/admin/page-content" element={<AdminPageContent />} />
            <Route path="/booking-confirmation" element={<BookingConfirmation />} />
            <Route path="/paypal-success" element={<PayPalSuccess />} />
            <Route path="/paypal-cancel" element={<PayPalCancel />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TranslationProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
